import { useCallback, useEffect, useRef, useState } from "react";
import { CloudClient, type ConnectedEnvironment, type RpcSocket, type TerminalSocket } from "./cloud";
import type { PublicConfig } from "./config";
import type { EnvironmentEvent, EnvironmentLink, WorkspaceSnapshot } from "./protocol";
import { parseSnapshot } from "./protocol";

export type ConnectionStatus =
  | "signed-out"
  | "loading"
  | "ready"
  | "connecting"
  | "connected"
  | "error";

export function useWorkspace(
  config: PublicConfig,
  userId: string | null,
  getClerkToken: () => Promise<string | null>,
) {
  const clientRef = useRef<CloudClient | null>(null);
  const environmentRef = useRef<ConnectedEnvironment | null>(null);
  const rpcRef = useRef<RpcSocket | null>(null);
  const terminalRef = useRef<TerminalSocket | null>(null);
  const reconnectTimer = useRef<number | null>(null);
  const reconnectAttempts = useRef(0);
  const connectRef = useRef<((environmentId?: string | null) => Promise<ConnectedEnvironment>) | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>(userId ? "loading" : "signed-out");
  const [environments, setEnvironments] = useState<EnvironmentLink[]>([]);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<string | null>(null);
  const [connection, setConnection] = useState<ConnectedEnvironment | null>(null);
  const [snapshot, setSnapshot] = useState<WorkspaceSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleEvent = useCallback((event: EnvironmentEvent) => {
    if (event.type === "Orchestration") setSnapshot(parseSnapshot(event.payload));
  }, []);
  const handleProtocolError = useCallback((reason: Error) => setError(reason.message), []);

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      clientRef.current?.clearSession().catch(() => undefined);
      clientRef.current = null;
      environmentRef.current = null;
      rpcRef.current?.close();
      rpcRef.current = null;
      terminalRef.current?.close();
      terminalRef.current = null;
      setEnvironments([]);
      setSelectedEnvironmentId(null);
      setConnection(null);
      setSnapshot(null);
      setError(null);
      setStatus("signed-out");
      return;
    }

    clientRef.current?.clearSession().catch(() => undefined);
    rpcRef.current?.close();
    rpcRef.current = null;
    terminalRef.current?.close();
    terminalRef.current = null;
    environmentRef.current = null;
    setConnection(null);
    setSnapshot(null);
    setEnvironments([]);
    const client = new CloudClient({ config, accountId: userId, getClerkToken, onEvent: handleEvent, onError: handleProtocolError });
    clientRef.current = client;
    setStatus("loading");
    setError(null);
    void client
      .listEnvironments()
      .then((links) => {
        if (cancelled) return;
        setEnvironments(links);
        setSelectedEnvironmentId((current) => current ?? links[0]?.environmentId ?? null);
        setStatus("ready");
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        setError(errorMessage(reason));
        setStatus("error");
      });
    return () => {
      cancelled = true;
      if (reconnectTimer.current !== null) window.clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
      environmentRef.current = null;
      rpcRef.current?.close();
      rpcRef.current = null;
      terminalRef.current?.close();
      terminalRef.current = null;
      void client.clearSession();
    };
  }, [config, getClerkToken, handleEvent, handleProtocolError, userId]);

  const connect = useCallback(async (environmentId = selectedEnvironmentId) => {
    const client = clientRef.current;
    const link = environments.find((entry) => entry.environmentId === environmentId);
    if (!client || !link || !environmentId) throw new Error("Select a linked environment first.");
    setStatus("connecting");
    setError(null);
    try {
      const connected = await client.connectEnvironment(environmentId);
      const nextSnapshot = await client.snapshot(connected);
      const rpc = await client.openRpc(connected, () => {
        if (environmentRef.current?.environmentId !== connected.environmentId) return;
        setStatus("error");
        setError("Environment connection lost. Reconnecting...");
        const retry = reconnectAttempts.current++;
        if (retry >= 6) {
          setError("Environment connection lost after six retries. Connect again to resume.");
          return;
        }
        const delay = Math.min(8_000, 500 * 2 ** retry);
        reconnectTimer.current = window.setTimeout(() => {
          void connectRef.current?.(environmentId).catch(() => undefined);
        }, delay);
      });
      await rpc.request("orchestration.subscribeShell", {});
      environmentRef.current = connected;
      rpcRef.current = rpc;
      setConnection(connected);
      setSnapshot(nextSnapshot);
      setSelectedEnvironmentId(environmentId);
      reconnectAttempts.current = 0;
      setStatus("connected");
      return connected;
    } catch (reason: unknown) {
      setError(errorMessage(reason));
      setStatus("error");
      throw reason;
    }
  }, [environments, selectedEnvironmentId]);

  connectRef.current = connect;

  const disconnect = useCallback(async () => {
    if (reconnectTimer.current !== null) window.clearTimeout(reconnectTimer.current);
    reconnectTimer.current = null;
    reconnectAttempts.current = 0;
    environmentRef.current = null;
    rpcRef.current?.close();
    rpcRef.current = null;
    terminalRef.current?.close();
    terminalRef.current = null;
    setConnection(null);
    setSnapshot(null);
    setStatus(environments.length ? "ready" : "error");
  }, [environments.length]);

  const dispatch = useCallback(async (command: unknown) => {
    const client = clientRef.current;
    const environment = environmentRef.current;
    if (!client || !environment) throw new Error("Connect an environment before sending a command.");
    try {
      return await client.dispatch(environment, command);
    } catch (reason: unknown) {
      setError(errorMessage(reason));
      throw reason;
    }
  }, []);

  const loadFiles = useCallback(async (cwd: string) => {
    const client = clientRef.current;
    const environment = environmentRef.current;
    if (!client || !environment) throw new Error("Connect an environment before browsing files.");
    return client.files(environment, cwd);
  }, []);

  const loadFile = useCallback(async (cwd: string, relativePath: string) => {
    const client = clientRef.current;
    const environment = environmentRef.current;
    if (!client || !environment) throw new Error("Connect an environment before reading files.");
    return client.file(environment, cwd, relativePath);
  }, []);

  const openTerminal = useCallback(async (terminalId: string, cwd: string, onEvent: (event: unknown) => void) => {
    const client = clientRef.current;
    const environment = environmentRef.current;
    if (!client || !environment) throw new Error("Connect an environment before opening a terminal.");
    terminalRef.current?.close();
    terminalRef.current = await client.openTerminal(environment, terminalId, cwd, 100, 28, onEvent);
    return terminalRef.current;
  }, []);

  const rpcRequest = useCallback(async (tag: string, payload: unknown) => {
    if (!rpcRef.current) throw new Error("Connect an environment before using its preview.");
    return rpcRef.current.request(tag, payload);
  }, []);

  const redeemPairing = useCallback(async (environmentUrl: string, pairingToken: string) => {
    const client = clientRef.current;
    if (!client) throw new Error("Sign in before pairing an environment.");
    const connected = await client.redeemPairing(environmentUrl, pairingToken);
    const nextSnapshot = await client.snapshot(connected);
    const rpc = await client.openRpc(connected, () => setStatus("error"));
    await rpc.request("orchestration.subscribeShell", {});
    environmentRef.current = connected;
    rpcRef.current = rpc;
    setEnvironments((current) => current.some((entry) => entry.environmentId === connected.environmentId) ? current : [...current, { environmentId: connected.environmentId, name: connected.environmentId, httpBaseUrl: connected.httpBaseUrl }]);
    setSelectedEnvironmentId(connected.environmentId);
    setConnection(connected);
    setSnapshot(nextSnapshot);
    setStatus("connected");
  }, []);

  return {
    status,
    environments,
    selectedEnvironmentId,
    setSelectedEnvironmentId,
    connection,
    snapshot,
    error,
    connect,
    disconnect,
    dispatch,
    loadFiles,
    loadFile,
    openTerminal,
    rpcRequest,
    redeemPairing,
  };
}

function errorMessage(reason: unknown): string {
  return reason instanceof Error ? reason.message : "The Codessa cloud connection failed.";
}
